import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import CallbackUploadDropdownButton from 'notebook/notes/previews/callback-oriented-google-drive-integration/UploadDropdownButton';

import RedirectUploadDropdownButton from './UploadDropdownButton';

// The two implementations expose an identical props contract, so the Switch is a
// drop-in for either. We source the type from the callback (default) component to
// keep existing callers type-checked against the path that ships today.
type UploadDropdownButtonProps = React.ComponentProps<typeof CallbackUploadDropdownButton>;
type RedirectUploadDropdownButtonProps = React.ComponentProps<typeof RedirectUploadDropdownButton>;

// Enforce the drop-in contract at compile time in BOTH directions. Spreading the
// callback props into the redirect component (below) only catches the redirect
// side *requiring* an extra prop; it would not catch the redirect side dropping or
// optionalizing a prop the callback side has. This bidirectional assignability
// check fails to compile if the two prop contracts ever diverge either way, so the
// Switch stays a true drop-in for either implementation. It is exported (rather
// than a local alias) so `noUnusedLocals` keeps it; it carries no runtime cost.
type AssertMutuallyAssignable<A extends B, B extends C, C = A> = never;
export type AssertUploadDropdownPropsMatch = AssertMutuallyAssignable<
  UploadDropdownButtonProps,
  RedirectUploadDropdownButtonProps
>;

/**
 * Flag-based switch between the two "Upload a File" dropdown implementations.
 *
 * Renders the redirect-oriented (popup OAuth) UploadDropdownButton when
 * `ELN_ENABLE_REDIRECT_ORIENTED_GOOGLE_DRIVE_FILE_PICKER` is enabled, and the
 * legacy callback-oriented (in-browser OAuth / DOM-mounted token) button
 * otherwise. The flag is default-off, so the callback path remains the default.
 *
 * Call sites import this single switch rather than either implementation
 * directly, so the redirect rollout is a one-line flip with no per-call-site
 * three-way drift.
 */
export default function UploadDropdownButtonSwitch(props: UploadDropdownButtonProps): JSX.Element {
  if (BenchlingConfig.ELN_ENABLE_REDIRECT_ORIENTED_GOOGLE_DRIVE_FILE_PICKER) {
    return <RedirectUploadDropdownButton {...props} />;
  }
  return <CallbackUploadDropdownButton {...props} />;
}
